// Controller for task-management

import { SelectableItem, SelectableStore, SelectableStoreDispatchers, SelectionDict, UniqKey } from '@/models/generics'
import { CtTask, TaskType } from '@/models/interfaces/content-type'
import { Status } from '@/models/status'
import { UUID } from '@Protium/vue-ozone-utils'
import { computed, onMounted, onUnmounted, reactive, Ref, ref, toRefs, watch } from 'vue'
import { useStore } from 'vuex'
import useInputListener from './useInputListener'
import type { InputListenerOptions, IntervalTimer } from './useInputListener'
import EAN from '@/utils/ean'
import { $get, $getArray, $set, debounce } from '@/utils'
import { Notify } from 'quasar'
import Directr, { ContentType } from '@/services/directr'
import Barcodes, { BarcodeTypeId } from '@/types/barcode'
import { now } from 'lodash'
import { ulid } from 'ulid'

export function validate1D (e, type?: BarcodeTypeId) {
  // EAN
  if ((e.length === 11 || e.length === 6) && e.split('').every(s => !isNaN(parseInt(s)))) e = `${'0'}${e}`
  const { valid, string, checkSum } = EAN.validate(e)

  // NOTE: returns original string if !valid
  return string
}

// COMBAK ideally the type is not nescesary
export function validate2D (e, type?: BarcodeTypeId) {
  const valid = type && Barcodes.Types.TwoDimensional.includes(String(type).toLowerCase() as any)
  if (valid) return e
  else return validate1D(e, type)
}

const __STORE = 'Tasks'
const DEFAULT_SELECTION_ID = 'sys.id'

export enum ScannableType {
  Product = 'product',
  Location = 'location',
  Any = 'any',
  Serial = 'serial',
  Stock = 'stock'
}

export interface TaskOptions extends Partial<Record<string, any>> {
  reference?: string
  scannable?: boolean
  socket?: boolean
  scannableOptions?: Partial<InputListenerOptions>
  onScanStart?: ((value: IntervalTimer | undefined) => void) | ((value: IntervalTimer | undefined) => Promise<void>),
  onScanEnd?: ((value: string, match: boolean) => void) | ((value: string, match: boolean) => Promise<void>),
  scannableType?: ScannableType | Array<ScannableType>
  nextSelectItemOn?: boolean | ((item: string | any, task: CtTask, metaValue?: any) => boolean)
  matchItemOn?(task: CtTask, item: UniqKey | Record<string, any>, string: string, match: Array<string>): boolean,
  clearOnComplete?: boolean,
  validateMetadataInput?(valid?: boolean, requiresMetadata?: boolean, taskItem?: any): Promise<boolean>,
  selectionId?: string
  debug?: boolean
  match?: Array<string> // array of fields/paths to match when executing match
  multi?: boolean // if multiple tasks can be active
  nextDelay?: number
  navigationStrategy?: 'friendly-neighbors' | 'absolute'
  fetch?: 'pending' | 'all'
  next?(search: string | boolean | undefined, match: TaskOptions['match'], tasks: Record<UniqKey, CtTask>, active: { id?: UniqKey | Array<UniqKey>, task?: CtTask }, select: SelectableStoreDispatchers['Select'], setActive: (id: UniqKey) => UniqKey | undefined, scan: boolean): void | boolean // replacement handler for default next function
}

function generateTaskItems<I extends (Record<string, any> | Array<any>), A = any> (item: I, valueTarget: string | ((item: I) => Array<A>)): Array<A> {
  if (typeof valueTarget === 'string') {
    const pathChunks = valueTarget.split('[].')
    return pathChunks[1] ? $getArray<I>(item, pathChunks[0], pathChunks[1]).flat(1) : $get<any>(item, pathChunks[0])
  }

  return valueTarget(item) || []
}

// Implements all basic functions, and utils for handling task progression.
// Companion to SelectableList. pass the state of this composable to selectableList for easy reactivity
// use composable useList as an input for SelectableList, which will provide automatic input for state.selection for example, to get easy creation of tasks from existing lists
export default function useTask<T extends Record<string, any> = any> (type: TaskType, options?: TaskOptions) {
  if (!type) throw Error('useTask requires a type argument')

  const $store = useStore()
  const userSettings = computed(() => $store.getters['User/Settings'])

  // create socket listener for specific task type
  const $directr: Directr = $store.getters['Services/Directr']
  if (options?.socket) {
    $directr.listen(`create/task:${type}`, payload => {
      void $store.dispatch(`${__STORE}/AddLocal`, { type, task: payload })
    })
    $directr.listen(`update/task:${type}`, payload => {
      void $store.dispatch(`${__STORE}/AddLocal`, { type, task: payload })
    })
  }

  // NOTE: for some reason whe tasks.value is observed directly it is an empty array, however when reading Object.keys(tasks.value), the actual object keys (task ids) are observed
  const tasks = computed<Record<UniqKey, CtTask>>(() => $store.getters[`${__STORE}/Dict`][type]) // all tasks by given type
  const taskKeys = computed<Array<UniqKey>>(() => Object.keys($store.getters[`${__STORE}/Dict`][type] as Record<UniqKey, CtTask> || {}) as Array<UniqKey>)
  const items = computed(() => Object.values(({ ...$store.getters[`${__STORE}/Dict`][type] as Record<UniqKey, CtTask> || {} })))
  const pendingItems = computed(() => (items.value || []).filter(f => !f.status?.includes(Status.Final) && !f.status?.includes(Status.Transiting)))

  // the active task
  const active = ref<UniqKey | Array<UniqKey> | undefined>(options?.multi ? [] : undefined) // the active task
  // keeps track of the current "neighbors" of the active value, meaning the next and previous item ids
  // set whenever active changes. not computed so that the value may be retained when active becomes null ("continue where you left off" behaviour)
  // this gives a consistent way to navigating even while mutating the list length
  // to use an "absolute" behaviour for next/previous items, pass options.navigationStrategy = 'absolute' // TODO: implement
  // formatted as: [previous,next]
  const activeNeighbors = ref<[UniqKey | undefined, UniqKey | undefined]>([undefined, undefined])

  // the last-known index of the active task
  const lastActiveTaskIndex = ref(-1)

  // watches active.value and registers the last activated task id
  watch(() => active.value, id => {
    // FIXME: this operates on the index of object keys, which have no guaranteed order
    const n = Array.isArray(id) ? id[id.length - 1] : id
    if (!n) return
    lastActiveTaskIndex.value = taskKeys.value.indexOf(n)
    activeNeighbors.value = [taskKeys.value[lastActiveTaskIndex.value - 1], taskKeys.value[lastActiveTaskIndex.value + 1]]
  })

  // the active (/focussed) item of the active task
  const activeTaskItem = ref<string>()
  const activeTask = computed(() => (active.value === null || active.value === undefined)
    ? undefined
    : (Array.isArray(active.value) ? active.value.map(a => $store.getters[`${__STORE}/Dict`][type]?.[a]) : $store.getters[`${__STORE}/Dict`][type]?.[active.value])
  )

  const identifier = ref<string>('sys.id')

  // NOTE returns the selection/selections for the current active task/tasks (hence "selections")
  const selections = computed<SelectionDict>(() => Object.fromEntries(
    (Array.isArray(active.value) ? active.value : [active.value])
      .map(id => ([id, $store.getters[`${__STORE}/Selections`] && $store.getters[`${__STORE}/Selections`][type] ? $store.getters[`${__STORE}/Selections`][type][id || ''] : undefined]))
      .filter(f => !!f[0])
  ))

  // clear selection for specified task
  async function clearSelection (task: UniqKey) {
    return await $store.dispatch(`${__STORE}/ClearSelection`, { type, task })
  }

  async function validateScan (scannableType: TaskOptions['scannableType'], data: any, type?: BarcodeTypeId) {
    if (!scannableType) return false
    const types = Array.isArray(scannableType) ? scannableType : [scannableType]

    let current: ScannableType | undefined = undefined
    let valid: any = false
    do {
      current = types.shift()
      switch (current) {
        case ScannableType.Any:
          valid = await validate2D(data, type)
          break
        case ScannableType.Location:
          valid = await validate2D(data, type)
          break
        case ScannableType.Product:
        case ScannableType.Stock:
        default:
          valid = await validate2D(data, type)
      }
    } while (current && (valid === false || valid === undefined || valid === null))

    return valid
  }

  // TODO: this is traveling correctly now, but the evaluator (i.e isSelectable) will always be false since the evaluation is never different (should check for ACTIVE)
  function evalSelectable (taskItem, evaluator: (value?: any) => boolean, commitSelection) {
    // FIXME: does not adhere to standard model
    const requiresMetadata = taskItem?.requires_metadata
    const valid = typeof evaluator === 'function' ? evaluator() : true

    if (!requiresMetadata) {
      // console.debug('No metadata required', valid)
      if (valid) commitSelection(taskItem)
    }

    if (!options?.validateMetadataInput || typeof options?.validateMetadataInput !== 'function') throw Error(`useTask task-item ${taskItem.id} required metadata, but options.validateMetadataInput was not a function`)

    if (requiresMetadata || !valid) {
      return options.validateMetadataInput(valid, requiresMetadata, taskItem).then(value => {
        // console.debug('metadata validated', value)
        if (value || (value === undefined && valid)) commitSelection(value)
        return value || (value === undefined && valid)
      }).catch(e => {
        console.error('evalSelectable | useTask metadata validation error', e)
      })
    }
  }

  const scanner = reactive({
    scannable: options?.scannable,
    listener: !options?.scannable ? undefined : useInputListener({
      start: (e, s) => {
        if (typeof options?.onScanStart === 'function') void options?.onScanStart(e)
      },
      end: (e, type, isScanner, isSingle, s) => {
        scanner.listener?.notify(e)
        let matched = false
        if (isScanner || isSingle && e?.length > 1) {
          void validateScan(options?.scannableType, e, type as BarcodeTypeId).then(valid => {
            if (valid) {
              // console.debug('validated scan', valid, e)
              nextItem(valid, options?.match || [], true).then(n => {
                // console.debug('scan nextItem', n)
                if (n === true || (typeof n === 'number' && n >= 0)) {
                  matched = true
                  return
                }
                if (e && (String(valid || '').toLocaleLowerCase() !== String(e || '').toLocaleLowerCase()))
                  void nextItem(e, options?.match || [], true)
                    .then(nx => matched = (nx === true || (typeof nx === 'number' && nx >= 0)))
              }).catch(console.error)
            }
          })
          // $directr.speak(DirectrEvent.Ping, e)
        }
        if (typeof options?.onScanEnd === 'function') void options?.onScanEnd(e, matched)
      }
    }, options?.scannableOptions || { createInput: true, debug: options?.debug, listenerId: 'use_task-' + ulid() })
  })

  // acts as a proxy to the store bound to this task. can act as the store argument for a SelectableList
  const listDispatchers: SelectableStoreDispatchers = {
    Add: async (id: string, items: Array<SelectableItem>) => {
      // add to task items
      const item = $store.getters[`${__STORE}/Dict`][type][id]
      if (!item) return
      const updated = (item.items || []).concat(items)
      return await $store.dispatch(`${__STORE}/Update`, { ...item, items: updated })
    },
    Remove: async (id: string, remove: number) => {
      // remove from task items
      const item = $store.getters[`${__STORE}/Dict`][type][id]
      if (!item) return
      const updated = (item.items || [])
      updated.splice(item.items.findIndex((item, i) => i === remove), 1)
      return await $store.dispatch(`${__STORE}/Update`, { ...item, items: updated })
    },
    Active: (id: string) => {
      setActive(id as UniqKey)
    },
    ActiveItem: (id: string) => {
      setActiveItem(id as UniqKey)
    },
    // FIXME: evaluator returning true or false means if you get the modal when clicking on select, figure out a way to make this dynamic based on what is needed.
    Select: async (task: string, selection: string | SelectionDict, value: boolean | number = false, toggle = false, evaluator = (e) => false) => {
      // FIXME: selection is being mutated unintentionally somewhere after newSelection is initially set
      // values are re-set here, including spreading newSelection in selectionData solving the issue but the source should be found
      // add / remove to task selection (toggle)
      const sl = typeof selection === 'string' ? String(selection) : { ...selection }
      const newKeys = typeof sl === 'string' ? String(sl) : [...Object.keys(sl)]
      const selectionDict = $store.getters[`${__STORE}/Selections`]
      const current: SelectionDict = selectionDict[type] ? selectionDict[type][task] : {}
      const newSelection = { ...current, ...(typeof sl === 'string' ? { [sl]: typeof value === 'boolean' ? value ? 1 : 0 : value } : sl) }

      if (toggle) {
        Object.keys(current).forEach(key => {
          if (newKeys.includes(key)) delete newSelection[key]
        })
      }

      const selectionData = {
        task,
        patch: $get(tasks.value[task], 'sys.type') === ContentType.Task,
        type,
        selection: { ...newSelection }
      }
      const commitSelection = async () => {
        return $store.dispatch(`${__STORE}/AddSelection`, selectionData)
      }
      const index = matchTaskItemIndex(tasks.value[task], typeof sl === 'string' ? sl : newKeys[0], options?.match || [])
      const itemDataRaw = tasks.value[task].items[index]

      // FIXME: passes a ref to the active task instead of the specific task, causing issues when mutating the active task in an external function
      const itemData = typeof itemDataRaw === 'string'
        ? active.value
          ? { id: itemDataRaw, ...tasks.value[active.value.toString()].meta[itemDataRaw] }
          : console.error('select | active task undefined')
        : itemDataRaw

      // NOTE value is the desired selection state (true / false or a numeric value)
      return value === false ? (await commitSelection() && value) : await evalSelectable(itemData, evaluator, commitSelection)

      // TODO: test if this return value is needed
      // return selected
    },
    Next: (input) => {
      next(input)
    },
    Complete: (id, clear?: boolean, clearDelay?: number) => {
      const task = typeof id === 'string' ? tasks.value[id] : id

      if (!id) throw Error('useTask.Complete called without valid id')
      // NOTE sets the "save" argument based on of the task has a sys.type field with value 'task' to prevent
      // items generated with @asTasks from triggering an api-call if their keys are uuids
      return $store.dispatch(`${__STORE}/Complete`, {
        type: task.type || type,
        task: $get(task, DEFAULT_SELECTION_ID, task.id),
        clear: clear || options?.clearOnComplete,
        clearDelay,
        save: $get(task, 'sys.type') === ContentType.Task,
        path: identifier.value
      })
    }
  }

  const listGetters: SelectableStore['getters'] = {
    Items: items,
    Selection: selections,
    Active: active,
    ActiveTask: activeTask,
    ActiveItem: activeTaskItem,

    Config: {
      type,
      options: { ...(options || {}), selectionId: options?.selectionId || DEFAULT_SELECTION_ID }
    }
  }

  function generateReference (): CtTask['reference'] {
    return `${options?.reference || ''}${Date.now().toString(16)}`
  }

  function clearActive (): void {
    active.value = undefined
  }

  function setActive<T extends UniqKey> (id: T): T | undefined {
    // check if the id is known first

    if (!taskKeys.value.includes(id)) {
      console.warn(`Tried to set key ${id} as active, but it is not a valid task id`)
      return undefined
    }

    if (options?.multi !== true) active.value = id
    else if (Array.isArray(active.value)) {
      if (active.value?.includes(id))
        active.value.splice(active.value?.indexOf(id))
      else active.value.push(id)
    } else active.value = [id]

    return id
  }

  function setActiveItem (id: string): string | undefined {
    activeTaskItem.value = id
    return activeTaskItem.value
  }

  async function init (clear?: boolean) {
    if (options?.fetch === 'all') await $store.dispatch(`${__STORE}/Fetch`, { type })
    else await $store.dispatch(`${__STORE}/Fetch`, { type, pending: true, replace: clear })
  }

  async function get (id: UUID) {
    return await $store.dispatch(`${__STORE}/Get`, id)
  }

  async function create<T = any> (items: Record<UUID | UniqKey, T> | Array<UniqKey | UUID>, meta: Record<string, any> = {}, status: Array<Status> = [Status.New]): Promise<Array<CtTask>> {
    const ref = generateReference()
    const task: Omit<CtTask, 'sys'> = {
      type,
      status,
      id: ref as UniqKey,
      reference: ref,
      items: Array.isArray(items) ? items : Object.keys(items) as Array<UniqKey>,
      meta
    }
    return await $store.dispatch(`${__STORE}/Add`, { type, task })
  }

  async function update (task: Partial<CtTask>) {
    return await $store.dispatch(`${__STORE}/Update`, { type, task })
  }

  // @delete
  async function cancel (task: UniqKey) {
    return await $store.dispatch(`${__STORE}/Cancel`, { type, task })
  }

  async function clear (t = type) {
    await $store.dispatch(`${__STORE}/Clear`, { type: t })
  }

  async function remove (task: UniqKey, t = type) {
    await $store.dispatch(`${__STORE}/Remove`, { type: t, task })
  }

  // String($get(i, m) || '').toLocaleLowerCase().trim() === string)
  function matchValue (source, value) {
    const match = String(value || '').toLocaleLowerCase().trim()
    const isEAN = EAN.validate(match).valid

    return Array.isArray(source)
      ? source.some(s => s && ((String(s || '').toLocaleLowerCase().trim() === match) || (isEAN && ((String(s || '').toLocaleLowerCase().trim() === match.slice(0, -1)) || (String(s || '').slice(0, -1) === match)))))
      : source && ((String(source || '').toLocaleLowerCase().trim() === match) || (isEAN && ((String(source || '').toLocaleLowerCase().trim() === match.slice(0, -1)) || (String(source || '').slice(0, -1) === match))))
  }

  function matchTaskItemIndex (task: CtTask, string: string, match: Array<string>, skipSelected?: boolean) {
    if (!task) {
      console.warn('Called matchTaskItemIndex without a valid task', task, string, match)
      return -1
    }

    const skippedIndexes: Array<number> = []
    let matchedIndex: number
    let retry = false

    // console.debug('matchTaskItemIndex', task.id, string, match)
    do {
      retry = false
      matchedIndex = task.items?.findIndex((i, n) => !skippedIndexes.includes(n) &&
        match.some(m => matchValue($get(i, m, i), string)) ||
        (typeof i === 'string' && (task.meta || {})[i] && match.some(m => matchValue($get((task.meta || {})[i] || {}, m), string))) || // search for item as key in meta object
        (
          task.meta && typeof task.meta === 'object' &&
          match.some(m => matchValue($get((task.meta || {}), m), string)) // meta search
        ) ||
        (typeof options?.matchItemOn === 'function' && options?.matchItemOn(task, i, string, match) === true)
      )

      const matchId = matchedIndex < 0 ? undefined : getTaskItemId(task.items[matchedIndex])
      if (matchId && selections.value[task.id]?.[matchId]) {
        skippedIndexes.push(matchedIndex)
        if (skippedIndexes.length === task.items.length) {
          matchedIndex = -1
          retry = false
        }
        else retry = true
      } else if (!matchId) matchedIndex = -1
      // console.log(task.items.length, retry ? 'retrying' : 'returning', matchedIndex, getTaskItemId(task.items[matchedIndex]), selections.value[task.id]?.[getTaskItemId(task.items[matchedIndex])])
    } while (retry)

    return matchedIndex
  }

  function getTaskItemId (item): string {
    return typeof item === 'object' ? $get(item, options?.selectionId || DEFAULT_SELECTION_ID) : item
  }

  /**
   *
   * @param search the search string. can be set to "false" to simply trigger the natural next item in th list
   * @param match array of paths to match against, defaults to options.match if set
   * @description find the next task by matching the reference or searching in items
   * if options.next is set, this is called first. this should return "true" or nothing if successfull, or "false" if the default handler should continue
   * if no item is active yet and no search is given, return the first task item
   */
  function next (search?: string | boolean, match: Array<string> = (options?.match || []), scan = false) {
    if (options?.next) {
      // if a next handler option is set, let that determine and set the next item instead
      const nextResult = options.next(
        search,
        match,
        tasks.value,
        { id: active.value, task: tasks.value[Array.isArray(active.value) ? active.value[0] : (active.value || '')] },
        listDispatchers.Select,
        setActive,
        scan
      )
      // NOTE if false is returned, this signals that next was unsuccessfull and the default handler should continue
      if (nextResult !== false) return true
    }

    // otherwise determine the next task based on if a search query was defined
    if (!active.value && !search) {
      setActive(taskKeys.value[0])
      return false
    } else if (active.value && !search) {
      // if there are multiple active items, switch to the next one
      if (Array.isArray(active.value)) {
        active.value.shift()
        return true
      }

      // navigate using set navigation strategy & delay
      navigate.next(options?.nextDelay)
      return true

      // const cIndex = taskKeys.value.indexOf(active.value) // COMBAK support for active array

      // if ((cIndex + 1) > (taskKeys.value.length - 1)) return
      // else if (cIndex >= 0) {
      //   if (options?.nextDelay && options?.nextDelay > 0) {
      //     // otherwise, set the next active key based on the next-navigation strategy, respecting the set value of nextDelay

      //     return setTimeout(() => setActive(taskKeys.value[cIndex + 1]), options?.nextDelay)
      //   } else return setActive(taskKeys.value[cIndex + 1])
      // }
    }

    // search for the first matching item by "search" in "match"
    const keys = taskKeys.value

    // search for first matching element in items of the active task
    // NOTE skips items in the "final" state
    // console.debug('keys', keys)
    const index = keys.findIndex(key =>
      !tasks.value[key].status.includes(Status.Final) &&
      (matchValue(tasks.value[key].id, search) ||
        matchValue(tasks.value[key].reference, search) ||
        tasks.value[key].items?.some(i =>
          match.some(m => matchValue($get(i, m), search)) || // direct item search
          (typeof i === 'string' && (tasks.value[key].meta || {})[i] && match.some(m => matchValue($get((tasks.value[key].meta || {})[i] || {}, m), search))) // search for item as key in meta object
        ) ||
        (tasks.value[key].meta && typeof tasks.value[key].meta === 'object' &&
          match.some(m => matchValue($get((tasks.value[key].meta || {}), m), search)) // meta search
        ))
    )

    if (index < 0) return false
    const select = matchTaskItemIndex(tasks.value[keys[index]], search as string, match)

    if (select >= 0) {
      // const itemId = typeof tasks.value[keys[index]].items[select] === 'object' ? tasks.value[keys[index]].items[select][options?.selectionId || DEFAULT_SELECTION_ID] : tasks.value[keys[index]].items[select]
      const itemId = getTaskItemId(tasks.value[keys[index]].items[select])
      const selection: SelectionDict = { [itemId]: true }

      // if the item is clear to be selected
      const isSelectable = !scan ? true : typeof options?.nextSelectItemOn === 'function'
        ? options.nextSelectItemOn(tasks.value[keys[index]].items[select], tasks.value[keys[index]])
        : typeof options?.nextSelectItemOn === 'boolean'
          ? options?.nextSelectItemOn
          : true

      if (isSelectable) void listDispatchers.Select(keys[index] as string, selection)
      else setActiveItem(itemId)
    }

    // TODO: check if navigation delay should be respected here...
    setActive(keys[index])
    return tasks.value[keys[index]]
  }


  function navigateToTask (nextIndexOrKey: number | UniqKey, fallback?: number, delay?: number) {
    let index = (typeof nextIndexOrKey === 'string') ? taskKeys.value?.indexOf(nextIndexOrKey) : nextIndexOrKey

    if (index === undefined || index < 0) {
      if (fallback !== undefined && fallback !== null) index = fallback
    }

    if (index === undefined || index < 0) return

    const nextDelay = typeof delay === 'number' ? delay : (options?.nextDelay || 0)

    if (nextDelay > 0) {
      return setTimeout(() => setActive(taskKeys.value[index]), options?.nextDelay)
    } else return setActive(taskKeys.value[index])
  }

  function navigateToNextTask (delay?: number) {
    if (options?.navigationStrategy === 'absolute' && lastActiveTaskIndex.value < 0 || lastActiveTaskIndex.value === undefined || lastActiveTaskIndex.value === null) {
      if (!taskKeys.value[0]) console.warn('navigateToNextTask[no active]: taskKeys was empty', taskKeys)
      setTimeout(() => setActive(taskKeys.value[0]), delay || 0) // default to the first task key
      return active.value
    }

    const nextIndex = options?.navigationStrategy === 'absolute' ? (lastActiveTaskIndex.value || 0) + 1 : (activeNeighbors.value[1] as any)
    if (Array.isArray(active.value) && active.value.length) { // if active is an array, cycle through active items instead
      if (!active.value?.length) console.warn('navigateToNextTask: active.value was array, but had no length', active)
      active.value.shift()
      return
    }

    if ((typeof nextIndex !== 'number' && !nextIndex) || (nextIndex > (taskKeys.value.length - 1))) return
    else if (typeof nextIndex === 'string' || nextIndex >= 0) navigateToTask(nextIndex, undefined, delay)
    else console.warn('navigateToNextTask: nextIndex was negative', nextIndex, taskKeys, active)
  }

  function navigateToPreviousTask (delay?: number) {
    if (options?.navigationStrategy === 'absolute' && lastActiveTaskIndex.value < 0 || lastActiveTaskIndex.value === undefined || lastActiveTaskIndex.value === null) {
      setTimeout(() => setActive(taskKeys.value[0]), delay || 0)
      return active.value
    }

    const prevIndex = options?.navigationStrategy === 'absolute' ? (lastActiveTaskIndex.value || 0) - 1 : (activeNeighbors.value[0] as any)

    if (Array.isArray(active.value) && active.value.length) {
      active.value.shift()
      return
    }

    // if list was mutated, and last index is out of bounds, default to the last item
    // boot the user back to the first task (lol this is basically a wrong-warp)
    if ((typeof prevIndex !== 'number' && !prevIndex) || prevIndex > (taskKeys.value?.length - 1)) navigateToTask(taskKeys.value.length - 1, undefined, delay)
    else if (typeof prevIndex === 'string' || (prevIndex <= (taskKeys.value.length - 1))) navigateToTask(prevIndex, undefined, delay)
    else navigateToTask(taskKeys.value.length - 1, undefined, delay)
  }

  const navigate = {
    next: debounce(navigateToNextTask, 50),
    prev: debounce(navigateToPreviousTask, 50),
    to: debounce(navigateToTask, 50),
    lastIndex: lastActiveTaskIndex,
    neighbors: activeNeighbors
  }

  // find the next task list item for task[id] and make it active
  // if options.multi is set and a id is not explicitly passed, the first active item is used
  // options.match example for OrderProducts:
  // ['products.ean', 'products.sku', 'products.vendor.ean', 'products.vendor.sku', 'products.components.id', 'products.components.sku', 'products.components.ean']
  async function nextItem (search: string | boolean, match: Array<string> = (options?.match || []), scan = false) {
    const item = active.value
    const task: CtTask = tasks.value[Array.isArray(item) ? item[0] : (item || '')]
    const s = String(search || '').toLocaleLowerCase().trim()

    // call options.next contidional function if defined, skipping the rest of this function if returning explicitly false
    if (options?.next) {
      const nextResult = options.next(search, match, tasks.value, { id: item, task }, listDispatchers.Select, setActive, scan)
      if (nextResult !== false) return true
    }
    // if (typeof search === 'boolean') return

    // if the current task-selection is null
    // or if there is no active item, search for the first task, with an item matching the search using next()
    if (!item || !task) return next(search, match, scan)

    // if there is an active item
    // search for first matching element in items of the active task (that is not already selected)
    const index = matchTaskItemIndex(task, s, match, true)

    // if no item is matched, and the current task-selection is not-null, simply return
    if (index < 0) {
      // if the current task selcetion is empty, attempt to call next
      const selectionDict = $store.getters[`${__STORE}/Selections`]
      const current: SelectionDict = selectionDict[type] ? selectionDict[type][Array.isArray(item) ? item[0] : (item || '')] : {}

      if (!current || !Object.keys(current).length)
        return next(search, match, scan)

      // since there is a current selection and the item is not matched, update the scanner notification to reflext the error/not-found status
      scanner.listener?.updateNotification(s, 'error')
      return false
    }

    // COMBAK the evaluator for @evalSelectable. kind of an obscuring pattern here...
    const isSelectable = (metaValue) => {
      // console.log('isSelectable', !scan ? true : typeof options?.nextSelectItemOn === 'function'
      // ? options.nextSelectItemOn(task.items[index], task, activeTaskItem)
      // : typeof options?.nextSelectItemOn === 'boolean'
      //   ? options?.nextSelectItemOn
      //   : true)
      return !scan ? true : typeof options?.nextSelectItemOn === 'function'
        ? options.nextSelectItemOn(task.items[index], task, activeTaskItem)
        : typeof options?.nextSelectItemOn === 'boolean'
          ? options?.nextSelectItemOn
          : true
    }

    const itemId = getTaskItemId(task.items[index])
    const selection: SelectionDict = { [itemId]: true }

    // update the selection / active state, depending on isSelectable (also updates SelectableList selection if you passed the selection to it)
    const selected = await listDispatchers.Select(item as string, selection, true, false, isSelectable)
    setActiveItem(getTaskItemId(task.items[index + (selected ? 1 : 0)]))

    return index
  }

  // @Lifecycle
  onMounted(() => {
    scanner.listener?.listener.create()
  })

  onUnmounted(() => {
    scanner.listener?.listener.destroy()
  })

  // --
  async function asTasks<A extends Record<string, any> = T> (
    listPayload: Array<A> | Ref<Array<A>>,
    valueTarget: string | ((item: A) => Array<any>),
    options?: { diff?: boolean, identifier?: string, reference?: string | ((item: A) => string), meta: any, store?: boolean, type?: TaskType, selected?: (item: any) => boolean }) {

    const list = (listPayload as Ref<Array<A>>).value ? (listPayload as Ref<Array<A>>).value : listPayload
    if (!Array.isArray(list)) throw Error(`Argument list was not an array (was ${!list ? 'undefined/null' : typeof list})`)

    const t = options?.type || type
    const taskList: Array<CtTask | undefined> = await Promise.all(list.map(async item => {
      // set the given identifier lookup path to the internal identifier path
      const items = generateTaskItems<A>(item, valueTarget).flat(1).filter(Boolean)
      if (!items?.length) {
        console.warn(`[useTask.asTasks] Task "${$get(item, options?.identifier || DEFAULT_SELECTION_ID)}" has no items`)
        return
      }

      const status = Array.isArray(item.status)
        ? (item.status.includes(Status.Resolved) || item.status.includes(Status.Final)) ? [Status.Final] : item.status.includes(Status.Failed) ? [Status.Failed] : [Status.New]
        : (item.status === Status.Resolved || item.status === Status.Final) ? [Status.Final] : item.status === Status.Failed ? [Status.Failed] : [Status.New]

      const task = $set({
        type: t,
        status,
        id: $get(item, options?.identifier || DEFAULT_SELECTION_ID), // optional identifier is used as the canonical identifier here as well
        reference: typeof options?.reference === 'function' ? options?.reference(item) : $get(item, options?.reference || 'reference'),
        items,
        meta: $get(item, options?.meta || '')
      }, identifier.value, $get(item, DEFAULT_SELECTION_ID, options?.identifier)) as CtTask

      // set the initial selection
      if (options?.selected && typeof options.selected === 'function') {
        const s = options.selected as (item: any) => boolean
        await $store.dispatch(`${__STORE}/AddSelection`, { type, task: task.id, selection: Object.fromEntries(items.map(item => [item.id, s(item) ? 1 : 0])) })
      }

      return task
    })).then(e => e.filter(Boolean))

    // store as taskList in $store
    if (options?.store === false) return taskList
    if (options?.identifier && options.identifier !== identifier.value) identifier.value = options.identifier // since store was defined the identifier must change if it was set
    // await clear()
    if (taskList) await $store.dispatch(`${__STORE}/AddLocal`, { type: t, task: taskList, path: identifier.value, diff: options?.diff }) // NOTE that path is always equal to the general identifier set to useTasks, since listDispatchers use this

    return $store.getters[`${__STORE}/Dict`][t]
  }

  function addLocal (task: CtTask) {
    return $store.dispatch(`${__STORE}/AddLocal`, { type: type || task.type, task, path: identifier.value, diff: options?.diff })
  }

  return reactive({
    //
    scanner,
    tasks,
    items,
    pendingItems,
    active,
    activeTask,
    activeTaskItem,
    setActive,
    clearActive,
    selections, // FIXME this gives the impression of returning selections for ALL tasks, while it only returns selections for ACTIVE tasks, needs rename to activeSelections
    clearSelection,
    next,
    navigate,
    nextItem,
    init,
    get,
    create,
    update,
    cancel,
    clear,
    remove,
    add: listDispatchers.Add,
    addLocal,
    complete: listDispatchers.Complete,
    asTasks,

    config: {
      type,
      options,
      __STORE,
      DEFAULT_SELECTION_ID
    },
    store: {
      getters: listGetters,
      // dispatch<A extends keyof SelectableStoreDispatchers = keyof SelectableStoreDispatchers> (action: A, ...args: Parameters<SelectableStoreDispatchers[A]>) { return listDispatchers[action](...args) }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      dispatch<A extends keyof SelectableStoreDispatchers = keyof SelectableStoreDispatchers> (action: A, ...args: [id: Parameters<SelectableStoreDispatchers[A]>[0], i?: Parameters<SelectableStoreDispatchers[A]>[1] | any, a?: Parameters<SelectableStoreDispatchers[A]>[2] | any, o?: Parameters<SelectableStoreDispatchers[A]>[3] | any, u?: Parameters<SelectableStoreDispatchers[A]>[4] | any]) { return listDispatchers[action](...args) }
    }

  })
}
